import type { AtsEntityTypeInfo, AtsFieldDescriptor } from "@/types/ats-explorer-api"

export interface GraphNode {
  id: string
  label: string
  group: string
  /** Field count when the schema is known, else null */
  fieldCount: number | null
  /** Whether this entity's associations are known (its schema was loaded) */
  resolved: boolean
}

export interface GraphEdge {
  id: string
  from: string
  to: string
  /** The field on `from` that points at `to` */
  field: string
  kind: "to-one" | "to-many"
}

export interface EntityGraph {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

/** Group colours for nodes, in the Hub's palette (navy core, then the catalog groups) */
export const GROUP_COLORS: Record<string, string> = {
  Core: "#9db2e0",
  Records: "#9db2e0",
  Jobs: "#4aa06d",
  Clients: "#db9a3c",
  "Placement & billing": "#9a86c9",
  "Pay & Bill": "#7f6bb3",
  Housing: "#c98b6b",
  Audit: "#8e98a8",
  System: "#8e98a8",
  "Activity & communication": "#5878c4",
  "Recruiting activity": "#5878c4",
  "Person sub-resources": "#6bb3a8",
  "Company sub-resources": "#6bb3a8",
}
export const DEFAULT_GROUP_COLOR = "#b6bdc8"

export function groupColor(group: string | null): string {
  return (group && GROUP_COLORS[group]) ?? DEFAULT_GROUP_COLOR
}

function normalize(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/** An embedded object's identifier: Loxo's "job.id" or JobAdder's "candidate.candidateId" */
const EMBEDDED_ID_PATH = /^([A-Za-z_]+)\.(?:id|\1Id)$/

export function isEmbeddedIdPath(path: string): boolean {
  return EMBEDDED_ID_PATH.test(path)
}

/**
 * Loxo/JobAdder records carry foreign keys ("job_id", "companyId", "person_global_status_id")
 * rather than typed associations. Resolve the key to an entity by name, allowing the
 * plural catalog name ("jobs", "companies") and a few irregular plurals.
 */
function entityForForeignKey(fieldPath: string, byNormalizedName: Map<string, string>): string | null {
  // An embedded object's id ("job.id", "candidate.candidateId") or a top-level key ("job_id", "companyId")
  const match = EMBEDDED_ID_PATH.exec(fieldPath) ?? /^(.+?)(?:_id|Id)$/.exec(fieldPath)
  if (!match) return null
  const stem = normalize(match[1])
  const candidates = [stem, `${stem}s`, `${stem}es`, stem.replace(/y$/, "ies"), stem === "person" ? "people" : stem, stem === "company" ? "companies" : stem]
  for (const candidate of candidates) {
    const hit = byNormalizedName.get(candidate)
    if (hit) return hit
  }
  return null
}

/**
 * The catalog entity a foreign key or embedded object points at, by name ("person.id" →
 * "people", "job_id" → "jobs"), or null when no entity matches. Shared by the entity
 * map and the record blade so both resolve untyped associations the same way.
 */
export function inferAssociatedEntity(fieldPath: string, entityNames: Iterable<string>): string | null {
  const byNormalizedName = new Map(Array.from(entityNames, (name) => [normalize(name), name] as const))
  const leaf = fieldPath.split(".").slice(-2).join(".")
  return entityForForeignKey(leaf, byNormalizedName)
}

/**
 * Builds the entity map from the tenant's entity types plus whatever schemas are known.
 * Bullhorn schemas name their association targets (edges are exact); Loxo and JobAdder
 * schemas are inferred, so edges come from foreign-key field names. Edges only point at
 * entities the tenant exposes.
 */
export function buildEntityGraph(entities: AtsEntityTypeInfo[], schemas: Map<string, AtsFieldDescriptor[]>): EntityGraph {
  const byNormalizedName = new Map(entities.map((e) => [normalize(e.name), e.name] as const))
  const edges = new Map<string, GraphEdge>()

  const nodes: GraphNode[] = entities.map((entity) => {
    const schema = schemas.get(entity.name)
    return {
      id: entity.name,
      label: entity.displayName,
      group: entity.group ?? "Records",
      fieldCount: schema ? schema.filter((f) => !f.isExpandable).length : null,
      resolved: !!schema,
    }
  })

  for (const [from, fields] of schemas) {
    if (!byNormalizedName.has(normalize(from))) continue
    for (const field of fields) {
      // Top-level associations only (plus an embedded object's id): deeper paths belong to the expanded entity's own map
      const depth = field.path.split(".").length
      if (depth > 2 || (depth === 2 && !isEmbeddedIdPath(field.path))) continue
      const explicit = field.associatedEntityType && byNormalizedName.get(normalize(field.associatedEntityType))
      const target = explicit || (!field.associatedEntityType ? entityForForeignKey(field.path, byNormalizedName) : null)
      if (!target || target === from) continue
      const kind: GraphEdge["kind"] = field.isArray || field.path.endsWith("[]") || field.kind === "ToMany" ? "to-many" : "to-one"
      const id = `${from}->${target}:${field.path}`
      if (!edges.has(id)) edges.set(id, { id, from, to: target, field: field.path.replace(/\[\]$/, "[]"), kind })
    }
  }

  return { nodes, edges: Array.from(edges.values()) }
}

/** Entities one hop from `id`, with the connecting fields */
export function neighboursOf(graph: EntityGraph, id: string): { node: GraphNode; via: GraphEdge[] }[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]))
  const via = new Map<string, GraphEdge[]>()
  for (const edge of graph.edges) {
    const other = edge.from === id ? edge.to : edge.to === id ? edge.from : null
    if (!other) continue
    const list = via.get(other)
    if (list) list.push(edge)
    else via.set(other, [edge])
  }
  return Array.from(via, ([other, edges]) => ({ node: byId.get(other)!, via: edges })).filter((n) => n.node)
}

/** Node ids that lie on the open blade path (selected entity plus the records drilled into) */
export function litPath(selected: string | null, openEntities: string[]): Set<string> {
  return new Set([selected, ...openEntities].filter((id): id is string => !!id))
}
